import { useState, useEffect } from "react";
import { X, ArrowRightLeft, Loader2, AlertTriangle, Lock } from "lucide-react";
import { apiRequest } from "../context/AuthContext";

const YEARS = ["2025", "2026", "2027", "2028", "2029"];

/**
 * Default-visible "Change Enrolment Year" control for the player dashboard.
 * Full recruitment-cycle reset: archives the current tracked_colleges/emails
 * state, resets every tracked college to initial outreach, and updates
 * profiles.target_start_year. See backend/routers/enrolment.py.
 *
 * A player cannot open a new switch while a prior one is still "open" —
 * i.e. not yet reverted by an admin AND still within its 30-day
 * revert_deadline. This is checked fresh on every mount/userId change by
 * fetching the player's switch history and looking for ANY entry meeting
 * that raw condition (not the display-only revert_eligible/superseded
 * flags, which only describe the single latest switch).
 */
export default function ChangeEnrolmentYearModal({ userId, currentYear, onSwitched, variant = "dark" }) {
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(currentYear || "");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [pendingSwitch, setPendingSwitch] = useState(null); // the open switch blocking a new one, if any
  const [checkingLock, setCheckingLock] = useState(true);

  useEffect(() => {
    if (!userId) { setCheckingLock(false); return; }
    setCheckingLock(true);
    apiRequest("get", `/players/${userId}/enrolment-year-switches`)
      .then(({ data }) => {
        const now = new Date();
        const open = (data || []).filter(
          s => !s.reverted && new Date(s.revert_deadline) > now
        );
        if (open.length) {
          // Legacy multi-switch data: use the most recent by switched_at.
          open.sort((a, b) => new Date(b.switched_at) - new Date(a.switched_at));
          setPendingSwitch(open[0]);
        } else {
          setPendingSwitch(null);
        }
      })
      .catch(() => setPendingSwitch(null))
      .finally(() => setCheckingLock(false));
  }, [userId]);

  const fmtDate = iso =>
    iso
      ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
      : "—";

  const openModal = () => {
    setYear(currentYear || "");
    setConfirm("");
    setError("");
    setOpen(true);
  };

  const handleConfirm = async () => {
    if (!year || year === currentYear) {
      setError("Choose a different year to switch to");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const { data } = await apiRequest(
        "post",
        `/players/${userId}/enrolment-year-switch`,
        { new_target_year: year }
      );
      setOpen(false);
      onSwitched?.(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not switch enrolment year. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!checkingLock && pendingSwitch) {
    return (
      <div
        data-testid="change-enrolment-year-locked"
        className={
          variant === "light"
            ? "bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 max-w-md"
            : "bg-white/10 border border-white/20 rounded-lg px-4 py-3 max-w-md"
        }
      >
        <div className={`flex items-center gap-2 text-sm font-bold uppercase tracking-wider ${variant === "light" ? "text-amber-800" : "text-white"}`}>
          <Lock className="w-4 h-4" />
          Switch Pending Admin Review
        </div>
        <p className={`text-xs mt-1.5 leading-relaxed ${variant === "light" ? "text-amber-800" : "text-white/80"}`}>
          You switched from <strong>{pendingSwitch.old_target_year}</strong> to{" "}
          <strong>{pendingSwitch.new_target_year}</strong> on {fmtDate(pendingSwitch.switched_at)}.
          This must be resolved by an admin, or will unlock automatically after{" "}
          <strong>{fmtDate(pendingSwitch.revert_deadline)}</strong>, before you can switch again.
        </p>
        <p className={`text-xs mt-1.5 ${variant === "light" ? "text-amber-800" : "text-white/80"}`}>
          Need this resolved sooner? Email{" "}
          <a href="mailto:support@getcourtbound.com" className="underline hover:opacity-80">
            support@getcourtbound.com
          </a>.
        </p>
      </div>
    );
  }

  return (
    <>
      <button
        data-testid="change-enrolment-year-btn"
        onClick={openModal}
        disabled={checkingLock}
        className={
          variant === "light"
            ? "bg-slate-900 text-white font-bold uppercase tracking-wider rounded-lg px-5 py-2.5 hover:bg-slate-700 transition-all text-sm flex items-center gap-2 disabled:opacity-60"
            : "bg-white/10 text-white font-bold uppercase tracking-wider rounded-lg px-5 py-2.5 hover:bg-white/20 transition-all text-sm flex items-center gap-2 border border-white/20 disabled:opacity-60"
        }
      >
        <ArrowRightLeft className="w-4 h-4" />
        {currentYear ? `Target Year: ${currentYear}` : "Set Target Year"}
      </button>

      {open && (
        <div
          data-testid="change-enrolment-year-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={e => { if (e.target === e.currentTarget && !submitting) setOpen(false); }}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-slate-900"
                  style={{ fontFamily: "Barlow Condensed, sans-serif", textTransform: "uppercase" }}>
                  Change Enrolment Year
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  Switch your Target Enrolment Year and start a fresh recruitment cycle.
                </p>
              </div>
              <button onClick={() => !submitting && setOpen(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-1.5">
                New Target Enrolment Year
              </label>
              <select
                data-testid="change-enrolment-year-select"
                value={year}
                onChange={e => { setYear(e.target.value); setConfirm(""); setError(""); }}
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none bg-white"
              >
                <option value="" disabled>Select a year</option>
                {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              {error && <p data-testid="change-enrolment-year-error" className="text-red-500 text-xs mt-1.5">{error}</p>}
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex gap-2.5">
              <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-amber-800 leading-relaxed space-y-1">
                <p>Switching your Target Enrolment Year is a full reset:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>
                    {currentYear ? `Your ${currentYear} activity` : "Your current activity"} — every email and follow-up —
                    moves to a new <strong>"{currentYear || "current year"} Activity"</strong> archive tab.
                  </li>
                  <li>Every college you've contacted restarts at <strong>initial outreach</strong>.</li>
                  <li>
                    This can be <strong>reverted by an admin</strong> — email{" "}
                    <a href="mailto:support@getcourtbound.com" className="underline hover:text-amber-900">
                      support@getcourtbound.com
                    </a>{" "}
                    within <strong>30 days</strong> to request it — after that it's permanent.
                  </li>
                </ul>
              </div>
            </div>

            {year && year !== currentYear && (
              <div className="mb-5">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-1.5">
                  Type <span className="text-orange-600 font-black">{year}</span> to confirm
                </label>
                <input
                  data-testid="change-enrolment-year-confirm-input"
                  type="text"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  placeholder={year}
                  autoFocus
                  className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm font-mono tracking-widest focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none placeholder:text-slate-300"
                />
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setOpen(false)}
                disabled={submitting}
                className="flex-1 border border-slate-300 text-slate-600 rounded-xl py-2.5 text-sm font-semibold hover:bg-slate-50 transition-all disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                data-testid="change-enrolment-year-confirm-btn"
                onClick={handleConfirm}
                disabled={submitting || confirm !== year}
                className="flex-1 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-xl py-2.5 text-sm hover:bg-orange-600 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {submitting
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Switching...</>
                  : <><ArrowRightLeft className="w-3.5 h-3.5" /> Confirm Switch</>
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
